import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { Card, Divider, Grid, Typography, Box, Stack, Container } from '@mui/material';

import { GetUserResponse } from 'types/user';
import Routes from 'constants/routes';

import { createStaff, fetchUserDetails, removeStaff, SetStatus } from 'redux/slices/staff/staffSlice';
import Button from 'components/Common/Button';
import AssistanceFooter from 'components/Common/AssistanceFooter';
import DetailItem from 'components/Staff/ItemDetail';
import Profile from 'components/Staff/Profile';
import Modal from 'components/Common/Modal';
import MuiLoader from 'components/Common/Loader';

export default function UserDetail() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    //   const [resetPasswordModal, setResetPasswordModal] = useState(false)
    const [openModal, setOpenModal] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusUpdated, setStatusUpdated] = useState(false);
    const [details, setDetails] = useState<any>({});
    const { username } = params || {};
    const { type, status } = location?.state || {};
    const { name, email, telephone, createdDate, city, state, zip, title, uuid, firstName, lastName, role } = details || {};

    useEffect(() => {
        if (username) {
            (async () => {
                setInitialLoading(true);
                const { payload }: { payload: GetUserResponse | unknown } = await dispatch(fetchUserDetails({ username }));
                setInitialLoading(false);
                if (payload) {
                    setDetails(payload);
                }
            })();
        }
    }, [username, dispatch]);

    const handleConfirm = useCallback(async () => {
        if (status === 'DELETED') {
            if (!username) {
                console.error('Reactivate-employee - username is not valid.');
                return;
            }
            const formValues = {
                firstName,
                lastName,
                email: username,
                employeeType: role,
            };
            setLoading(true);
            const { payload } = await dispatch(createStaff({ formValues: { ...formValues, employeeType: 'CLERK' } }));
            setLoading(false);
            if (payload?.code === 200) {
                setStatusUpdated(true);
            }
        } else if (status === 'ACTIVE') {
            if (!uuid) {
                console.error('Suspend-employee - UUID is not valid.');
                return;
            }
            setLoading(true);
            const { payload } = await dispatch(removeStaff(uuid));
            setLoading(false);
            if (payload) {
                setOpenModal(false);
                navigate(Routes.staff.main);
            }
        }
    }, [dispatch, username, firstName, lastName, role, uuid, navigate, status]);

    const modalContent = useMemo(() => {
        if (statusUpdated) {
            return (
                <>
                    <Typography sx={{ fontSize: 18, my: 2 }}>
                        An invitation email has been sent to the email listed for this employee.{' '}
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button onClick={() => navigate(Routes.staff.main)} width={152} title="Done" />
                    </div>
                </>
            );
        }
        return (
            <>
                {status !== 'DELETED' ? (
                    <Typography sx={{ fontSize: 18, my: 2 }}>
                        Once you disable an employee they will nolonger be able to log into the portal. You will be able to reactivate the
                        employee through the “Inactive” employee section.
                        <br />
                        Are you sure you would like to disable this employee?
                    </Typography>
                ) : (
                    <Typography sx={{ fontSize: 18, my: 2 }}>
                        Once you reactivate this employee they will be sent a reactivation email and will have the ability to log into this
                        portal.
                        <br />
                        Are you sure you want to reactivate this employee?
                    </Typography>
                )}

                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                    <Button onClick={() => setOpenModal(false)} width={'45%'} title="Cancel" />
                    <Button loading={loading} onClick={handleConfirm} width={'45%'} title="Confirm" />
                </div>
            </>
        );
    }, [status, statusUpdated, handleConfirm, loading, navigate]);

    return (
        <Container>
            <Card
                sx={{
                    width: '100%',
                    p: '2rem',
                    my: 1,
                }}
            >
                <Stack m={'auto'} width={{ xs: '100%', md: '80%' }}>
                    <MuiLoader absolute loading={initialLoading} />
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2 }}>
                        <Typography sx={{ fontSize: 16, color: '#1F2937', fontWeight: 400 }} variant="h1">
                            {type === 'employee' ? 'Employee' : 'UUID'} # {details?.uuid}
                        </Typography>

                        {/* CAN BE ENABLED WHEN EDIT EMPLOYEE OR STAFF API IS READY */}
                        {/* <Link to="/staff/details-edit">
                  <EditIcon style={{ cursor: 'pointer' }} />
              </Link> */}
                    </Box>

                    <Divider />
                    <Profile my={6} user={details} />
                    <Grid container>
                        {/* Left Container */}
                        <Grid item xs={6}>
                            <DetailItem label="First Name" value={firstName} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailItem label="Email/user name" value={email || username} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailItem label="Last Name" value={lastName} />
                        </Grid>
                        {type === 'employee' && (
                            <Grid item xs={6}>
                                <DetailItem label="Role" value={title} />
                            </Grid>
                        )}

                        {/* <DetailItem label="Date added" value={dateFormat(createdDate)} />
                <DetailItem label="Phone" value={telephone} /> */}

                        {/* Right Container */}
                        {/* <Grid item xs={12} md={5}>
                  <DetailItem label="Address" value={getAddress(details)} />
                  <DetailItem label="City" value={city} />
                  <DetailItem label="State" value={state} />
                  <DetailItem label="Zip" value={zip} />
              </Grid> */}
                    </Grid>
                    {type === 'employee' && (
                        <Stack justifyContent={'center'} direction={'row'} mt={5}>
                            <Button
                                sx={{ width: { xs: '100%', sm: '45%' }, fontSize: { xs: 16, xl: 20 } }}
                                onClick={() => setOpenModal(true)}
                                title={status === 'DELETED' ? 'Reactivate Employee ' : 'Suspend Employee'}
                            />
                        </Stack>
                    )}

                    {/* Reset Password Modal */}
                    {/* <Modal isOpen={resetPasswordModal} onClose={() => setResetPasswordModal(false)}>
                      <>
                          <Typography sx={{ fontSize: 18, my: 2 }}>
                              A link to change password has been sent to the email address listed for this user. <br />
                          </Typography>
                          <div
                              style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'space-around',
                                  marginTop: 32,
                              }}
                          >
                              <Button onClick={() => setResetPasswordModal(false)} width={152} title="Done" />
                          </div>
                      </>
                  </Modal> */}
                    {/* Employee Modal */}
                    <Modal isOpen={openModal} onClose={() => setOpenModal(false)}>
                        {modalContent}
                    </Modal>
                    <AssistanceFooter />
                </Stack>
            </Card>
        </Container>
    );
}
